import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ONE_STOP_PATH } from 'domain/shared/oneStop';
import { ProjectTeamType, useProjectTeamService } from 'domain/shared/newProject';
import { StaffingDataType } from 'domain/shared/staffing';
import { FormFieldsData, FormShowTemplate } from 'framework/form';
import { EditableListFormShowTemplate } from 'framework/list';
import { useShowContext } from 'framework/show';

import { DashboardItemSectionWrapper } from 'framework/ui/dashboard';
import RequiredPositionsList from './RelatedRolesSection';
import RequiredPositionsSectionHeader from './RequiredPositionsSectionHeader';
import { mapTeamStructureWithAssignments } from 'domain/shared/assignment/mapTeamStructureWithAssignments';

const RequiredPositionsWrapper = () => {
  const navigate = useNavigate();
  const { data, setData } = useShowContext<StaffingDataType>();
  const projectData = data.getData();
  const { createProjectTeam, updateProjectTeam, getProjectTeamValidationSchema } = useProjectTeamService();

  const enrichInputData = useCallback(
    (team?: ProjectTeamType) => {
      return mapTeamStructureWithAssignments(projectData, team);
    },
    [projectData],
  );
  const getDataFunc = useCallback(
    async () => Promise.resolve(enrichInputData(projectData.team)),
    [enrichInputData, projectData.team],
  );

  const submitFormFunc = async (formData: FormFieldsData) => {
    const requestBody = {
      id: formData.team.id,
      projectId: projectData.id,
      location: formData.team.location,
      type: formData.team.type,
      expectedStructure: formData.team.expectedStructure,
    };

    const submitFunc = formData.team?.id ? updateProjectTeam : createProjectTeam;
    return await submitFunc(requestBody).then(response => enrichInputData(response));
  };

  return (
    <FormShowTemplate
      key={`required-positions-${projectData.id}-${(projectData.assignments || []).map(assignment => assignment.id).join('|')}`}
      isInitialsLoadingPosition
      getValidationSchemaFunc={getProjectTeamValidationSchema}
      submitFormFunc={submitFormFunc}
      afterSubmit={{
        message: 'Required positions have been successfully updated.',
        execute: async response => {
          setData?.({
            ...projectData,
            ...response,
          });
        },
      }}
      getDataFunc={getDataFunc}
    >
      <EditableListFormShowTemplate key={`key-${projectData.id}`} listSource="team.expectedStructure">
        <DashboardItemSectionWrapper
          sx={{ '.MuiAccordionSummary-root': { pr: 0 } }}
          title={<RequiredPositionsSectionHeader onRequestVacancy={() => navigate(`${ONE_STOP_PATH}/REC04-NEW`)} />}
          defaultExpanded
        >
          <RequiredPositionsList projectData={projectData} setProjectData={setData} />
        </DashboardItemSectionWrapper>
      </EditableListFormShowTemplate>
    </FormShowTemplate>
  );
};

export default RequiredPositionsWrapper;
